/**
 * Custom Cart drawer — <custom-cart>
 *
 * Markup: snippets/custom-cart-drawer.liquid, owned by sections/custom-cart.liquid.
 * Styles: assets/custom-cart.css.
 *
 * Ported from the Skivys in-house cart drawer and adapted to Impulse:
 *   - re-renders through the Section Rendering API endpoint `?sections=custom-cart`
 *   - drives Impulse's own `.cart-link__bubble` / `.cart-link__bubble-num` header
 *     indicator instead of Skivys' `#CartCount`
 *   - formats money through `theme.Currency.formatMoney` and the shop's real
 *     money_format rather than a hardcoded USD formatter
 *   - adds the policy agreement gate, which blocks checkout until the shopper
 *     ticks the box (see `policyBlocked` / `window.CustomCartPolicy`)
 *
 * Deliberately NOT ported: Skivys' `clearDiscountAndAttributes` /
 * `checkAndClearDiscountIfNoShopTheLook` pair. Those exist to clean up after that
 * store's "Shop The Look" bundle app, which Ride Tech Moto does not run — there is
 * no `_shop_the_look` or `_discount_code` line property anywhere in this theme.
 * Carrying them over would cost six network requests per cart change and would
 * actively wipe a discount code a shopper had legitimately applied.
 *
 * Only two regions are swapped on a cart mutation: the innerHTML of
 * `.custom-cart__body` and of `#custom-cart-footer`. Anything that depends on cart
 * state must live inside one of those two.
 *
 * Also owns two line-level features the Skivys original did not have:
 *   - the policy agreement gate (`policyBlocked`, `window.CustomCartPolicy`)
 *   - the lifetime warranty, added as a NATIVE Shopify nested cart line
 *   - the package upgrade switch (`switchPackage`), which moves a line between
 *     the values of its "Choose Package" option in either direction
 *
 * Exposes:
 *   window.CustomCartPolicy = { required(), blocked(), flag() }
 */
class CustomCart extends HTMLElement {
  constructor() {
    super();
    // Mirrors the merchant's free shipping threshold (in cents) as rendered into
    // data-threshold by snippets/custom-cart-drawer.liquid. All threshold and
    // progress maths is done Liquid-side and stays there; this field exists so JS
    // consumers can read the same value the current markup was rendered with.
    this.threshold = parseInt(this.dataset.threshold, 10) || 0;
    this.isUnlockedState = false;
    this.confettiAnimId = null;
    this.productCache = {};
    this.isDetailView = false;
    // Policy agreement lives here rather than in the markup: the footer's
    // innerHTML is replaced on every cart mutation, so a checkbox ticked before a
    // quantity change would otherwise silently untick itself.
    this.policyAccepted = false;
    this.noteTimer = null;

    // Every cart mutation runs through `enqueue`, one at a time. Shopify's cart
    // endpoints are not transactional against each other: two overlapping
    // /cart/change.js calls, or a package switch's add+remove pair interleaved
    // with a quantity change, resolve in whatever order the network delivers.
    this.mutationChain = Promise.resolve();

    // Bumped by every mutation and carried into the section render it triggers.
    // A render whose token is no longer current was rendered from a cart that has
    // since moved on, so it is discarded rather than painted over the newer one.
    this.renderSeq = 0;

    // lineKey -> the quantity the shopper has actually asked for, which is ahead
    // of the rendered DOM while a round-trip is in flight.
    this.pendingQty = new Map();
  }

  connectedCallback() {
    this.panel = this.querySelector('.custom-cart__panel');
    this.overlay = this.querySelector('.custom-cart__overlay');
    this.canvas = this.querySelector('.custom-cart__confetti-canvas');

    this.bindEvents();
    this.applyConvertedThreshold();
    this.checkInitialState();
    this.syncPolicyState();

    window.CustomCartPolicy = {
      required: () => this.policyRequired(),
      blocked: () => this.policyBlocked(),
      flag: () => this.flagPolicyError(),
    };
  }

  /* ------------------------------------------------------------- utilities */

  t(key, fallback = '') {
    const strings = (window.customCart && window.customCart.translations) || {};
    return strings[key] || fallback;
  }

  /**
   * Formats an integer number of cents using the shop's own money_format, via the
   * currency helper Impulse already ships. Every caller passes a value straight
   * out of Shopify's JS cart / product APIs, which express money as cents.
   *
   * The fallback only runs if theme.js has not parsed yet, which is possible
   * because this file is loaded independently of it.
   */
  formatMoney(cents) {
    const amount = Number(cents);
    if (!Number.isFinite(amount)) return '';

    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      return theme.Currency.formatMoney(amount, theme.settings && theme.settings.moneyFormat);
    }

    return '$' + (amount / 100).toFixed(2);
  }

  /**
   * The class the merchant's "Button style" setting resolved to, published by
   * snippets/custom-cart-drawer.liquid. Used for the buttons this file builds —
   * the variant-picker confirm and the product-detail add-to-cart — which the
   * Liquid cannot class for us.
   */
  primaryButtonClass(fallback) {
    return (window.customCart && window.customCart.primary_button_class) || fallback;
  }

  routeRoot() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  /**
   * Merchandise units only — the shipping protection fee is a real cart line and
   * must never show up in the header count. Falls back to the raw Shopify count
   * when assets/custom-cart-protection.js is absent or protection is switched off.
   */
  merchandiseCount(cart) {
    const filters = window.CustomCartFilters;
    if (filters && typeof filters.merchandiseCount === 'function') {
      return filters.merchandiseCount(cart);
    }
    return cart ? cart.item_count : 0;
  }

  /**
   * Announces every cart render on the document. `source` lets a listener that
   * caused the mutation recognise and skip its own event instead of reacting to
   * it and looping.
   */
  dispatchCartUpdated(cart, source) {
    document.dispatchEvent(
      new CustomEvent('custom-cart:updated', {
        bubbles: true,
        detail: { cart: cart, source: source || 'custom-cart' },
      })
    );
  }

  /**
   * External cart mutation (shipping protection). Re-renders from the section
   * endpoint, then re-announces with the caller's source so the caller can ignore
   * its own event instead of looping.
   */
  async renderFromExternal(cart, source) {
    const seq = ++this.renderSeq;
    this.currentCart = cart;
    if (!this.isDetailView) {
      await this.renderCartState(seq);
    }
    this.updateHeaderBadge(this.merchandiseCount(cart));
    this.dispatchCartUpdated(cart, source);
  }

  /* ------------------------------------------------------- policy agreement */

  /** The checkbox is only rendered when the merchant has enabled it. */
  policyRequired() {
    return !!this.querySelector('[data-policy-checkbox]');
  }

  policyBlocked() {
    const checkbox = this.querySelector('[data-policy-checkbox]');
    return !!checkbox && !checkbox.checked;
  }

  /**
   * Re-points the checkbox at the remembered decision after the footer's
   * innerHTML has been replaced, and mirrors the blocked state onto the host and
   * the checkout button so CSS can dim the accelerated checkout buttons.
   */
  syncPolicyState() {
    const checkbox = this.querySelector('[data-policy-checkbox]');
    if (checkbox) {
      checkbox.checked = this.policyAccepted;
    }

    const blocked = this.policyBlocked();
    this.setAttribute('data-policy-blocked', blocked ? 'true' : 'false');

    const button = this.querySelector('[data-custom-cart-checkout]');
    if (button) {
      button.classList.toggle('custom-cart__checkout-btn--blocked', blocked);
      button.setAttribute('aria-disabled', blocked ? 'true' : 'false');
    }

    if (!blocked) this.clearPolicyError();
  }

  flagPolicyError() {
    const row = this.querySelector('[data-policy-row]');
    const target = this.querySelector('[data-policy-error]');

    if (row) {
      // Removed and re-added on the next frame so the shake animation replays on
      // a second click rather than only firing once per render.
      row.classList.remove('custom-cart__policy--error');
      requestAnimationFrame(() => row.classList.add('custom-cart__policy--error'));
    }

    if (target) {
      target.textContent = this.t('policy_required', 'Please agree to the policy before checking out.');
    }

    const checkbox = this.querySelector('[data-policy-checkbox]');
    if (checkbox) checkbox.focus();
  }

  clearPolicyError() {
    const row = this.querySelector('[data-policy-row]');
    if (row) row.classList.remove('custom-cart__policy--error');

    const target = this.querySelector('[data-policy-error]');
    if (target) target.textContent = '';
  }

  /* ---------------------------------------------------------------- confetti */

  triggerFallingConfetti() {
    // Merchant setting. Only an explicit `false` disables it, so a missing flag
    // (older markup, section not yet re-rendered) keeps the default behaviour.
    if (window.customCart && window.customCart.confetti_enabled === false) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const canvas = this.canvas || this.querySelector('.custom-cart__confetti-canvas');
    if (!canvas) return;

    const cardEl = this.querySelector('.custom-cart__card') || canvas.parentElement;
    const width = cardEl ? cardEl.clientWidth : 400;
    const height = cardEl ? cardEl.clientHeight : window.innerHeight;

    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    const particleCount = 100;
    const particles = [];
    // Ride Tech Moto's palette: the red primary and white, with the blue sale
    // accent and a gold for lift.
    const colors = ['#cf2424', '#ffffff', '#df4747', '#b92020', '#0b82e2', '#ffd700', '#d9d9d9'];

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * width,
        y: -15 - Math.random() * 120, // start above the top of the card
        width: 6 + Math.random() * 7,
        height: 10 + Math.random() * 8,
        color: colors[Math.floor(Math.random() * colors.length)],
        vx: (Math.random() - 0.5) * 2.2,
        vy: 2.8 + Math.random() * 4,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.2,
        oscillationSpeed: 0.03 + Math.random() * 0.05,
        oscillationAmplitude: 1.2 + Math.random() * 1.8,
      });
    }

    if (this.confettiAnimId) {
      cancelAnimationFrame(this.confettiAnimId);
    }

    let frameCount = 0;
    const maxFrames = 240; // ~4s at 60fps

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      frameCount++;
      let activeParticles = 0;

      particles.forEach((p) => {
        if (p.y < canvas.height + 30) {
          activeParticles++;
          p.y += p.vy;
          p.x += p.vx + Math.sin(frameCount * p.oscillationSpeed) * p.oscillationAmplitude;
          p.rotation += p.rotationSpeed;

          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rotation);
          ctx.fillStyle = p.color;
          ctx.fillRect(-p.width / 2, -p.height / 2, p.width, p.height);
          ctx.restore();
        }
      });

      if (activeParticles > 0 && frameCount < maxFrames) {
        this.confettiAnimId = requestAnimationFrame(animate);
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    };

    animate();
  }

  /* ------------------------------------------------------------------ events */

  bindEvents() {
    /**
     * Checkout gate. Capture phase on document, and registered before the
     * delegated click handler below, so it runs first.
     *
     * stopImmediatePropagation is what makes this safe alongside
     * assets/custom-cart-protection.js, which also intercepts the checkout click
     * to flush a pending protection sync: whichever of the two is registered
     * first, a blocked checkout stops here. The protection module additionally
     * asks `window.CustomCartPolicy.blocked()` and bails, covering the case where
     * its own listener happens to run first.
     */
    document.addEventListener(
      'click',
      (event) => {
        if (!event.target.closest('[data-custom-cart-checkout]')) return;
        if (!this.policyBlocked()) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.flagPolicyError();
      },
      true
    );

    document.addEventListener('click', (event) => {
      // Cart triggers and any link pointing at the cart page.
      const cartLink =
        event.target.closest('.js-drawer-open-cart') ||
        event.target.closest('[data-custom-cart-toggle]') ||
        event.target.closest('a[href$="/cart"]') ||
        event.target.closest('a[href*="/cart?"]');

      if (cartLink) {
        const href = cartLink.getAttribute('href') || '';
        // The cart page stays authoritative on itself, mirroring Impulse's own
        // `if (!document.body.classList.contains('template-cart'))` guard — a
        // drawer over the cart page gives two editable copies of one cart.
        const onCartPage = document.body.classList.contains('template-cart');
        if (!href.includes('/checkout') && !onCartPage) {
          event.preventDefault();
          this.openCart();
          return;
        }
      }

      if (event.target.closest('[data-cart-close]')) {
        event.preventDefault();
        this.closeCart();
        return;
      }

      if (event.target.closest('[data-cart-back]')) {
        event.preventDefault();
        this.hideProductDetails();
        return;
      }

      const showDetailsBtn = event.target.closest('[data-fbt-show-details]');
      if (showDetailsBtn) {
        event.preventDefault();
        const handle = showDetailsBtn.dataset.handle;
        if (handle) this.showProductDetails(handle);
        return;
      }

      const fbtAtcBtn = event.target.closest('[data-fbt-atc]');
      if (fbtAtcBtn) {
        event.preventDefault();
        const handle = fbtAtcBtn.dataset.handle;
        const variantId = fbtAtcBtn.dataset.variantId;
        const hasVariants = fbtAtcBtn.dataset.hasVariants === 'true';

        if (handle && hasVariants) {
          this.openVariantModal(handle);
        } else if (variantId) {
          this.addVariant(variantId, fbtAtcBtn);
        } else if (handle) {
          this.openVariantModal(handle);
        }
        return;
      }

      if (event.target.closest('[data-variant-modal-close]')) {
        event.preventDefault();
        this.closeVariantModal();
        return;
      }

      const confirmBtn = event.target.closest('[data-variant-confirm]');
      if (confirmBtn) {
        event.preventDefault();
        const select = this.querySelector('[data-variant-select]');
        if (select && select.value) {
          this.addVariant(select.value, confirmBtn);
          this.closeVariantModal();
        }
        return;
      }

      const qtyBtn = event.target.closest('[data-qty-change]');
      if (qtyBtn) {
        event.preventDefault();
        const lineKey = qtyBtn.dataset.lineKey;
        const change = parseInt(qtyBtn.dataset.qtyChange, 10);

        // Count from what has already been asked for, not from what is on screen.
        // Reading `data-current-qty` alone meant two clicks inside one round-trip
        // both saw the old number, both asked for old+1, and the second request
        // changed nothing — so a double click moved the quantity by one.
        const base = this.pendingQty.has(lineKey)
          ? this.pendingQty.get(lineKey)
          : parseInt(qtyBtn.dataset.currentQty, 10) || 0;
        const next = Math.max(0, base + change);

        this.pendingQty.set(lineKey, next);
        this.paintQuantity(qtyBtn, next);

        // A nested child has no stepper of its own — it tracks the product it hangs
        // off, so two helmets never end up with one warranty. Shopify cascades
        // REMOVAL to children but does not mirror quantity, so this part stays
        // manual. The drawer only emits the key when the child's own
        // `instructions.can_update_quantity` allows it.
        // Comma separated: a line can carry more than one nested add-on. Empty
        // segments are filtered, because the Liquid emits a trailing comma.
        const linked = (qtyBtn.dataset.nestedChildKeys || '')
          .split(',')
          .map((k) => k.trim())
          .filter(Boolean);
        this.updateLineItem(lineKey, next, linked.length ? linked : null);
        return;
      }

      // "Pairs well with" (snippets/product-complementary.liquid). A plain
      // <div class="btn"> with a `product-id` attribute — not a form, so the
      // submit interception never sees it. Its only binding used to live in
      // theme.AjaxProduct, which theme.js does not construct on this cart type,
      // so the button silently did nothing.
      const complementaryBtn = event.target.closest('[data-add-to-cart-complemetary]');
      if (complementaryBtn) {
        event.preventDefault();
        if (complementaryBtn.getAttribute('aria-busy') === 'true') return;

        const complementaryId = complementaryBtn.getAttribute('product-id');
        if (complementaryId) {
          // Same private property the theme's own handler wrote, so attribution
          // on existing orders keeps matching. The typo is the theme's.
          this.addVariant(complementaryId, complementaryBtn, { _reference: 'Paris Well With' });
        }
        return;
      }

      const removeBtn = event.target.closest('[data-remove-item]');
      if (removeBtn) {
        event.preventDefault();
        // Removing a parent takes its add-ons with it. Shopify cascades this for
        // its own nested lines, but the drawer pairs them itself and cannot assume
        // the native relationship took, so the children are named explicitly.
        // Removing an add-on on its own just drops that add-on.
        const linked = (removeBtn.dataset.nestedChildKeys || '')
          .split(',')
          .map((k) => k.trim())
          .filter(Boolean);
        this.updateLineItem(removeBtn.dataset.lineKey, 0, linked.length ? linked : null);
        return;
      }

      /**
       * Click-to-toggle on the policy text.
       *
       * The text is a <div> wired to the checkbox with aria-labelledby rather than
       * a <label for>, because the agreement copy is rich text and normally
       * carries a policy link — inside a label, clicking that link both follows it
       * AND toggles the box, so the box appears to tick itself. Anchors are
       * skipped here and left to navigate on their own.
       */
      const policyText = event.target.closest('[data-policy-text]');
      if (policyText && !event.target.closest('a')) {
        const checkbox = this.querySelector('[data-policy-checkbox]');
        if (checkbox) {
          checkbox.checked = !checkbox.checked;
          this.policyAccepted = checkbox.checked;
          this.syncPolicyState();
        }
        return;
      }
    });

    /**
     * One delegated change listener, because the footer's innerHTML is replaced
     * on every cart mutation — a listener bound to the checkbox itself would be
     * orphaned by that swap.
     */
    this.addEventListener('change', (event) => {
      const checkbox = event.target.closest('[data-policy-checkbox]');
      if (checkbox) {
        this.policyAccepted = checkbox.checked;
        this.syncPolicyState();
        return;
      }

      const upgradeToggle = event.target.closest('[data-upgrade-toggle]');
      if (upgradeToggle) {
        // Both directions. Switching off is a real cart mutation now — it puts the
        // line back on the variant recorded in its `_upgrade_from` property.
        this.switchPackage(upgradeToggle);
        return;
      }

      const note = event.target.closest('[data-cart-note]');
      if (note) this.updateNote(note.value);
    });

    /**
     * Intercept every product form submission targeting /cart/add.
     *
     * Capture phase on document, deliberately: Impulse binds submit directly on
     * each product form via theme.AjaxProduct. A handler bound to the target
     * element runs before a bubble-phase listener on document, so both would fire
     * and the item would be POSTed twice. Capturing on document puts this first,
     * and stopPropagation stops the event before it reaches the form, making this
     * drawer the sole owner of add-to-cart.
     *
     * theme.AjaxProduct is not even constructed while `cart_type` is `custom`
     * (theme.js gates it on `cartType === 'drawer'`), so this is also what makes
     * add-to-cart ajax at all — without it the form would do a full page POST.
     */
    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (form.action && form.action.includes('/cart/add')) {
          event.preventDefault();
          event.stopPropagation();
          this.submitAddToCartForm(new FormData(form), form);
        }
      },
      true
    );

    // theme.CartDrawer exposed these as a hook for apps and for console use
    // ("Dev-friendly way to open cart"). It is not constructed on this cart type,
    // so honour them here rather than leaving a documented hook dead.
    document.addEventListener('cart:open', () => this.openCart());
    document.addEventListener('cart:close', () => this.closeCart());

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !this.classList.contains('is-open')) return;

      const modal = this.querySelector('.custom-cart__variant-modal.is-open');
      if (modal) {
        this.closeVariantModal();
        return;
      }
      this.closeCart();
    });

    window.addEventListener('resize', () => {
      const canvas = this.canvas || this.querySelector('.custom-cart__confetti-canvas');
      if (!canvas) return;
      const cardEl = this.querySelector('.custom-cart__card') || canvas.parentElement;
      canvas.width = cardEl ? cardEl.clientWidth : 400;
      canvas.height = cardEl ? cardEl.clientHeight : window.innerHeight;
    });
  }

  /**
   * Converts a per-market free shipping threshold written as [USD] in the custom
   * cart section. When the cart is in a currency other than the shop's, Liquid
   * can't convert, so it renders data-convert-usd and this swaps in
   * USD x Shopify.currency.rate (the store's Markets rate), rounded up to a whole
   * unit. Runs on first paint and after every section re-render, before the
   * unlocked/confetti check reads the state.
   */
  applyConvertedThreshold() {
    const usd = parseFloat(this.dataset.convertUsd);
    const banner = this.querySelector('.custom-cart__shipping-banner');
    if (!usd || !banner) return;

    const msgEl = banner.querySelector('[data-free-shipping-msg]');
    const rate = parseFloat(window.Shopify && Shopify.currency && Shopify.currency.rate);

    if (msgEl && rate) {
      // Round to cents first so float noise (38.0000001) doesn't bump the value up
      const threshold = Math.ceil(Math.round(usd * rate * 100) / 100) * 100;
      const total = parseInt(msgEl.dataset.cartTotal, 10) || 0;
      const unlocked = total >= threshold;
      const pct = Math.min(100, (total * 100) / threshold);

      this.threshold = threshold;
      this.dataset.threshold = threshold;
      window.customCart = window.customCart || {};
      window.customCart.free_shipping_threshold = threshold;

      msgEl.dataset.unlocked = unlocked ? 'true' : 'false';
      const lockedMsg = msgEl.querySelector('[data-msg-locked]');
      const unlockedMsg = msgEl.querySelector('[data-msg-unlocked]');
      if (lockedMsg) lockedMsg.style.display = unlocked ? 'none' : '';
      if (unlockedMsg) unlockedMsg.style.display = unlocked ? '' : 'none';
      const neededEl = msgEl.querySelector('[data-needed-money]');
      if (neededEl && !unlocked) neededEl.textContent = this.formatMoney(threshold - total);

      const track = banner.querySelector('.custom-cart__progress-track');
      const fill = banner.querySelector('[data-progress-fill]');
      if (track) track.setAttribute('aria-valuenow', Math.round(pct));
      if (fill) {
        fill.style.setProperty('--progress-width', pct + '%');
        fill.classList.toggle('custom-cart__progress-fill--unlocked', unlocked);
      }
    }

    // No rate available: keep the per-market fallback that Liquid rendered
    banner.style.visibility = '';
  }

  checkInitialState() {
    const msgEl = this.querySelector('[data-free-shipping-msg]');
    if (msgEl) {
      this.isUnlockedState = msgEl.dataset.unlocked === 'true';
    }
  }

  /* ------------------------------------------------------------- open / close */

  showLoading() {
    const overlay = this.querySelector('.custom-cart__loading-overlay');
    if (overlay) overlay.classList.add('is-loading');
  }

  hideLoading() {
    const overlay = this.querySelector('.custom-cart__loading-overlay');
    if (overlay) overlay.classList.remove('is-loading');
  }

  openCart() {
    this.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    document.body.classList.add('custom-cart-open');

    if (this.panel) this.panel.setAttribute('aria-hidden', 'false');
    if (this.panel) this.panel.focus();

    if (this.isUnlockedState) {
      this.triggerFallingConfetti();
    }
  }

  closeCart() {
    this.classList.remove('is-open');
    document.body.style.overflow = '';
    document.body.classList.remove('custom-cart-open');

    if (this.panel) this.panel.setAttribute('aria-hidden', 'true');

    this.hideProductDetails();
    this.closeVariantModal();
  }

  /* ------------------------------------------------------------ cart mutations */

  /**
   * Runs `work` after every mutation queued before it has settled.
   *
   * `.then(run, run)` rather than `.then(run)`: a rejected link must not poison
   * the chain, or one failed request would wedge the cart for the life of the
   * page.
   *
   * Callers already inside the queue must NOT call another enqueueing method —
   * that awaits a chain the caller is itself a link in, which deadlocks. They use
   * `fetchAndApply` / `applyCart` directly instead.
   */
  enqueue(work) {
    const run = () => work();
    this.mutationChain = this.mutationChain.then(run, run);
    return this.mutationChain;
  }

  /** Paints a cart onto the drawer. Assumes the caller holds the queue. */
  async applyCart(cart, seq) {
    this.currentCart = cart;
    if (!this.isDetailView) {
      await this.renderCartState(seq);
    }
    this.updateHeaderBadge(this.merchandiseCount(cart));
    this.dispatchCartUpdated(cart, 'custom-cart');
  }

  /** Re-reads the cart and paints it. Assumes the caller holds the queue. */
  async fetchAndApply(seq) {
    const res = await fetch(this.routeRoot() + 'cart.js', { headers: { Accept: 'application/json' } });
    await this.applyCart(await res.json(), seq);
  }

  /**
   * Shows the new quantity before the network answers.
   *
   * Also moves `data-current-qty` forward, so that even if the shopper clicks
   * again before the render lands, the DOM they are clicking is not lying about
   * what is in the cart.
   */
  paintQuantity(button, quantity) {
    const row = button.closest('.custom-cart__item');
    if (!row) return;

    const numEl = row.querySelector('.custom-cart__qty-num');
    if (numEl) numEl.textContent = quantity;

    row.querySelectorAll('[data-qty-change]').forEach((el) => {
      el.dataset.currentQty = quantity;
    });
  }

  refreshCart() {
    return this.enqueue(async () => {
      const seq = ++this.renderSeq;
      this.showLoading();
      try {
        await this.fetchAndApply(seq);
      } catch (err) {
        console.error('Custom cart: error refreshing cart', err);
      } finally {
        this.hideLoading();
      }
    });
  }

  /**
   * @param lineKey    line to set
   * @param quantity   absolute quantity
   * @param linkedKeys other lines to move to the same quantity — the warranty
   *                   attached to this product. Changed in the same queued unit,
   *                   so nothing can render between the two.
   */
  updateLineItem(lineKey, quantity, linkedKeys) {
    return this.enqueue(async () => {
      const seq = ++this.renderSeq;
      this.showLoading();
      try {
        // An absolute quantity, not a delta, so a superseded request is harmless:
        // the last one to run states the final number outright.
        let cart = await this.postCartChange(lineKey, quantity);

        for (const linked of linkedKeys || []) {
          // Line keys are stable across a quantity change, so the key read at
          // render time is still valid here.
          cart = await this.postCartChange(linked, quantity);
        }

        await this.applyCart(cart, seq);
      } catch (err) {
        console.error('Custom cart: error updating line item', err);
        // The optimistic number is now a claim the cart does not back. Drop the
        // intent and let the next render restate the truth.
        this.pendingQty.delete(lineKey);
        await this.renderCartState();
      } finally {
        this.hideLoading();
      }
    });
  }

  async postCartChange(lineKey, quantity) {
    const res = await fetch(this.routeRoot() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: quantity }),
    });
    return res.json();
  }

  /**
   * Order note. Debounced, because this fires on every `change` of the textarea
   * and the note is not worth a round-trip per keystroke-batch. Deliberately does
   * not re-render: replacing the footer would blow away the textarea the shopper
   * is still typing in.
   */
  updateNote(value) {
    clearTimeout(this.noteTimer);
    this.noteTimer = setTimeout(() => {
      fetch(this.routeRoot() + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: value }),
      }).catch((err) => console.error('Custom cart: error saving cart note', err));
    }, 400);
  }

  /**
   * Moves a cart line between its package variants, in either direction.
   *
   * The switch's `data-target-variant-id` is wherever it currently points — one
   * step up when off, back to the remembered original when on. Direction is read
   * from the post-change checked state, which also decides what happens to the
   * `_upgrade_from` line item property:
   *
   *   switching on  -> stamp `_upgrade_from` with the variant being left behind,
   *                    so the snippet can render the switch as on and offer the
   *                    way back
   *   switching off  -> drop the property, so the line is indistinguishable from
   *                    one that was never upgraded
   *
   * Shopify's cart cannot repoint a line at a different variant, so each move is
   * an add of the target followed by a keyed remove of the old line — the same
   * technique the shipping protection tier change uses. Keyed, not indexed:
   * /cart/add.js appends and renumbers nothing, but any other mutation landing in
   * between would, and the remove would then hit the wrong row.
   *
   * The add runs first on purpose. If it fails (the variant sold out between
   * render and click) the shopper still has the line they started with, where
   * removing first would leave them with nothing.
   */
  switchPackage(toggle) {
    // Queued as one unit: this is a read, an add and a remove, and a quantity
    // change landing between the add and the remove would have its own render
    // overwritten — or worse, retarget a line key that is about to disappear.
    return this.enqueue(() => this.runPackageSwitch(toggle));
  }

  async runPackageSwitch(toggle) {
    const seq = ++this.renderSeq;
    const lineKey = toggle.dataset.lineKey;
    const targetId = toggle.dataset.targetVariantId;
    const quantity = parseInt(toggle.dataset.quantity, 10) || 1;
    if (!lineKey || !targetId) return;

    const upgrading = toggle.checked;
    const row = toggle.closest('[data-upgrade-row]');
    const errorEl = row ? row.querySelector('[data-upgrade-error]') : null;
    if (errorEl) errorEl.textContent = '';

    toggle.disabled = true;
    this.showLoading();

    try {
      // Read the cart fresh rather than trusting this.currentCart, which is unset
      // until the first mutation on a page that was server-rendered. The line's
      // properties have to come across with it or a personalisation the shopper
      // entered would be silently dropped by the move.
      const cartRes = await fetch(this.routeRoot() + 'cart.js', { headers: { Accept: 'application/json' } });
      const cart = await cartRes.json();
      const existing = (cart.items || []).find((item) => item.key === lineKey);

      // Add-ons hanging off the line being switched. They have to be moved across
      // deliberately: the old line is about to be removed, and Shopify's nested
      // cart lines delete a parent's children with it.
      const oldGroup = existing && existing.properties
        ? existing.properties[CustomCart.NEST_GROUP_KEY]
        : null;
      const children = oldGroup
        ? (cart.items || []).filter(
            (item) => item.properties && item.properties[CustomCart.NEST_PARENT_KEY] === oldGroup
          )
        : [];

      const properties = Object.assign({}, (existing && existing.properties) || {});
      if (upgrading) {
        properties[CustomCart.UPGRADE_ORIGIN_KEY] = String(existing ? existing.variant_id : '');
      } else {
        delete properties[CustomCart.UPGRADE_ORIGIN_KEY];
      }

      // A fresh token for the new parent. Re-using the old one would make the
      // re-added add-on identical to the one still in the cart — same variant, same
      // properties — and a merge there would leave one line claiming two parents.
      const newGroup = children.length ? this.newNestGroup() : null;
      if (newGroup) {
        properties[CustomCart.NEST_GROUP_KEY] = newGroup;
      }

      const addRes = await fetch(this.routeRoot() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: Number(targetId),
              quantity: quantity,
              // An empty object would add a line with no properties, which is what
              // we want on the way back down; `undefined` keeps the payload clean.
              properties: Object.keys(properties).length ? properties : undefined,
            },
          ],
        }),
      });
      const added = await addRes.json();
      if (added && added.status && Number(added.status) >= 400) {
        throw new Error(added.description || added.message || 'Package change failed');
      }

      // Re-attach the add-ons to the new line, BEFORE the old one is removed, so
      // the cascade that follows only takes the old copies.
      if (children.length) {
        const newParentKey = this.addedLineKey(added);

        for (const child of children) {
          const childProps = Object.assign({}, child.properties || {});
          childProps[CustomCart.NEST_PARENT_KEY] = newGroup;

          const childLine = {
            id: child.variant_id,
            quantity: child.quantity,
            properties: childProps,
          };
          if (newParentKey) {
            childLine.parent_line_key = newParentKey;
          }

          try {
            const childRes = await fetch(this.routeRoot() + 'cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: [childLine] }),
            });
            const childJson = await childRes.json();
            if (childJson && childJson.status && Number(childJson.status) >= 400) {
              throw new Error(childJson.description || childJson.message || 'Add-on move failed');
            }
          } catch (childErr) {
            // Not rethrown: the package change itself succeeded and the shopper
            // should keep it. Logged loudly because a silently dropped add-on is
            // lost revenue.
            console.error(
              'Custom cart: could not carry an add-on across the package change',
              childErr
            );
          }
        }
      }

      // The old copies go by hand rather than on Shopify's cascade. The cascade
      // only fires if `parent_line_key` took; if it did not, the old copies would
      // survive carrying a `_cc_parent` that no line claims — skipped by the
      // drawer, still charged at checkout. Failures are swallowed because the
      // cascade may legitimately have removed them already.
      for (const child of children) {
        try {
          await this.postCartChange(child.key, 0);
        } catch (childErr) {
          console.warn('Custom cart: old add-on copy was already gone', childErr);
        }
      }

      const updated = await this.postCartChange(lineKey, 0);

      await this.applyCart(updated, seq);
    } catch (err) {
      console.error('Custom cart: package change failed', err);
      // Put the switch back where it was — leaving it flipped would claim a
      // package the cart does not hold.
      toggle.checked = !upgrading;
      toggle.setAttribute('aria-checked', toggle.checked ? 'true' : 'false');
      if (errorEl) {
        errorEl.textContent = this.t('upgrade_error', 'We could not change that package. Please try again.');
      }
    } finally {
      toggle.disabled = false;
      this.hideLoading();
    }
  }

  /**
   * @param variantId  variant to add
   * @param buttonEl   optional control to put into a loading state
   * @param properties optional line item properties to carry onto the line
   */
  addVariant(variantId, buttonEl, properties) {
    this.setAddButtonBusy(buttonEl, true);

    // Opened outside the queue: the drawer should slide in on the click, not after
    // whatever mutation is currently in flight has finished.
    this.isDetailView = false;
    this.openCart();

    return this.enqueue(async () => {
      const seq = ++this.renderSeq;
      this.showLoading();
      try {
        const line = { id: variantId, quantity: 1 };
        // Omitted entirely rather than sent empty: an empty object still creates a
        // line with a properties bag, which changes how Shopify groups identical
        // lines together.
        if (properties && Object.keys(properties).length) {
          line.properties = properties;
        }

        const res = await fetch(this.routeRoot() + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [line] }),
        });
        const added = await res.json();
        if (added && added.status && Number(added.status) >= 400) {
          throw new Error(added.description || added.message || 'Add to cart failed');
        }
        // Not refreshCart(): that enqueues, and awaiting the queue from inside it
        // would deadlock.
        await this.fetchAndApply(seq);
      } catch (err) {
        console.error('Custom cart: error adding variant to cart', err);
        alert(err.message || this.t('sold_out', 'Sold out'));
      } finally {
        this.hideLoading();
        this.setAddButtonBusy(buttonEl, false);
      }
    });
  }

  /**
   * Loading state for whichever control triggered an add.
   *
   * The upsell buttons in the drawer are <button><span>label</span></button> and
   * swap their label. The "Pairs well with" control is a bare
   * <div class="btn">Add</div> with no span and no disabled semantics, so it gets
   * Impulse's own `.btn--loading` class and aria-busy instead of a text swap.
   */
  setAddButtonBusy(buttonEl, busy) {
    if (!buttonEl) return;

    const span = buttonEl.querySelector('span');
    if (span) {
      buttonEl.disabled = busy;
      span.innerText = busy ? this.t('adding', 'Adding...') : this.t('add_to_cart', 'Add to cart');
      return;
    }

    buttonEl.classList.toggle('btn--loading', busy);
    buttonEl.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  submitAddToCartForm(formData, formEl) {
    // Open before any network work. Awaiting the add, then cart.js, then the
    // section render before opening means three sequential round-trips during
    // which nothing happens on screen. The drawer now slides in on the click with
    // its spinner up and fills in as the responses land.
    this.isDetailView = false;
    this.openCart();

    return this.enqueue(() => this.runAddToCartForm(formData, formEl));
  }

  async runAddToCartForm(formData, formEl) {
    const seq = ++this.renderSeq;
    this.showLoading();

    // Read before posting, because the block is on the page the shopper is
    // leaving.
    const warranty = this.readWarrantySelection(formEl);

    // The group token has to travel WITH the product line: a line's properties
    // cannot be changed once it exists. /cart/add accepts them as
    // `properties[key]` form fields, so everything else the theme's form carries —
    // selling plans, engravings, other properties — is preserved untouched.
    let nestGroup = null;
    if (warranty) {
      nestGroup = this.newNestGroup();
      formData.append('properties[' + CustomCart.NEST_GROUP_KEY + ']', nestGroup);
    }

    try {
      const res = await fetch(this.routeRoot() + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      const added = await res.json();
      // /cart/add.js answers 4xx with a JSON body rather than rejecting, so a
      // sold-out or unavailable variant has to be caught here or refreshCart()
      // would quietly re-render the unchanged cart as if it had worked.
      if (added && added.status && Number(added.status) >= 400) {
        throw new Error(added.description || added.message || 'Add to cart failed');
      }
      if (warranty) {
        await this.addNestedWarranty(warranty, added, formData, nestGroup);
      }

      // Not refreshCart(): see the note in addVariant.
      await this.fetchAndApply(seq);

      // Same event, same shape, same target as theme.AjaxProduct's own success
      // handler, so anything listening for it keeps working while that class is
      // switched off. theme.CartDrawer is the only in-theme listener and it is not
      // constructed on this cart type, so this cannot loop back into a second open.
      document.dispatchEvent(
        new CustomEvent('ajaxProduct:added', {
          detail: {
            product: added,
            addToCartBtn: formEl ? formEl.querySelector('.add-to-cart') : null,
          },
        })
      );
    } catch (err) {
      console.error('Custom cart: error submitting add to cart form', err);
      this.closeCart();
      alert(err.message || this.t('sold_out', 'Sold out'));
    } finally {
      this.hideLoading();
    }
  }

  /**
   * Impulse renders a dot, not a number, and only shows a count when a
   * `.cart-link__bubble-num` element is present. Mirrors theme.CartForm's own
   * updateCount so the header behaves identically whichever cart is active.
   */
  updateHeaderBadge(count) {
    document.querySelectorAll('.cart-link__bubble-num').forEach((el) => {
      el.innerText = count;
    });

    document.querySelectorAll('.cart-link__bubble').forEach((bubble) => {
      bubble.classList.toggle('cart-link__bubble--visible', count > 0);
    });
  }

  /* ------------------------------------------------------- lifetime warranty */

  /**
   * The warranty offer belonging to a given product form, if it is ticked.
   *
   * Matched on the form's id rather than by DOM proximity: a quick-shop modal for
   * another product can be sitting in the page with its own warranty block, and
   * picking "the nearest one" would attach the wrong warranty — or attach one at
   * all when the shopper never saw the box.
   */
  /**
   * Locates the warranty block belonging to a product form.
   *
   * Returns { block, how } or null. `how` names the strategy that found it, which
   * is what the debug helper reports.
   */
  findWarrantyBlock(formEl) {
    if (!formEl) return null;

    // 1. Same product section. The form and the block are both inside the
    //    `[data-section-id]` wrapper by construction, so this holds regardless of
    //    what the form's id is, and stays correct on a page with several product
    //    sections (quick-shop modals) rather than picking the first in the
    //    document.
    const section = formEl.closest('[data-section-id]');
    if (section) {
      const scoped = section.querySelector('[data-lifetime-warranty]');
      if (scoped) return { block: scoped, how: 'section' };
    }

    // 2. Paired explicitly by form id.
    if (formEl.id) {
      const paired = document.querySelector(
        '[data-lifetime-warranty][data-warranty-form="' + formEl.id + '"]'
      );
      if (paired) return { block: paired, how: 'form-id' };
    }

    // 3. Last resort: the only one on the page, so there is nothing to confuse it
    //    with. Warned about, because reaching here means 1 and 2 both failed and
    //    the markup is not what this code expects.
    const all = document.querySelectorAll('[data-lifetime-warranty]');
    if (all.length === 1) {
      console.warn(
        'Custom cart: warranty block found only by falling back to the single block on the page. ' +
          'The form/block pairing is not matching — form id is ' +
          JSON.stringify(formEl.id) +
          ', block expects ' +
          JSON.stringify(all[0].getAttribute('data-warranty-form')) +
          '.'
      );
      return { block: all[0], how: 'only-one' };
    }

    return null;
  }

  /**
   * The warranty for this product form, if the shopper ticked it.
   *
   * Logs why it declined rather than returning a bare null: a warranty that
   * silently fails to attach is lost revenue, and the reason is not otherwise
   * observable from outside.
   */
  readWarrantySelection(formEl) {
    const found = this.findWarrantyBlock(formEl);
    if (!found) {
      if (document.querySelector('[data-lifetime-warranty]')) {
        console.warn('Custom cart: a warranty block exists but none could be matched to this form.');
      }
      return null;
    }

    const toggle = found.block.querySelector('[data-warranty-toggle]');
    if (!toggle) {
      console.warn('Custom cart: warranty block has no [data-warranty-toggle] checkbox.');
      return null;
    }

    // Not ticked is the normal case, not a problem — no logging.
    if (!toggle.checked) return null;

    const variantId = toggle.dataset.warrantyVariantId;
    if (!variantId) {
      console.warn(
        'Custom cart: warranty is ticked but the block carries no ' +
          'data-warranty-variant-id, so there is nothing to add.'
      );
      return null;
    }

    return { variantId: variantId, how: found.how };
  }

  /**
   * Token pairing a parent line with its add-ons. Time-ordered so it sorts
   * naturally, with a random tail so two adds in the same millisecond — a double
   * click, two tabs — cannot collide and cross-link.
   */
  newNestGroup() {
    return 'g' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  }

  /**
   * The line key of the product that was just added.
   *
   * /cart/add.js has answered in two shapes over the years: the bare line item
   * object, and `{ items: [...] }`. Both are handled because which one comes back
   * depends on the storefront's API version, not on anything this theme controls.
   */
  addedLineKey(added) {
    if (!added) return null;
    if (Array.isArray(added.items) && added.items.length) {
      return added.items[0].key || null;
    }
    return added.key || null;
  }

  /**
   * Attaches the warranty to the product that was just added, as a native nested
   * cart line.
   *
   * `parent_line_key` rather than `parent_id`: `parent_id` only works when parent
   * and child go up in the same request, and the product has to be posted as
   * FormData on its own to preserve everything the theme's form carries — selling
   * plans, engravings, other line item properties. So the product goes first and
   * the warranty names its key.
   *
   * Never rethrows. The product is in the cart and the shopper should see that; a
   * failure here means the cart shows no warranty, which is the truth. Logged
   * loudly, because otherwise it is silent lost revenue.
   */
  async addNestedWarranty(warranty, added, formData, nestGroup) {
    // Matches the product's quantity, so two helmets get two warranties.
    const quantity = parseInt(formData.get('quantity'), 10) || 1;

    const line = {
      id: Number(warranty.variantId),
      quantity: quantity,
      properties: { [CustomCart.NEST_PARENT_KEY]: nestGroup },
    };

    // Shopify's own nesting, on top of our properties. This is what nests the line
    // in checkout, on the order status page and in the emails. Sent only when the
    // parent's key came back — the drawer does not depend on it, so a missing key
    // costs the native nesting, not the feature.
    const parentKey = this.addedLineKey(added);
    if (parentKey) {
      line.parent_line_key = parentKey;
    } else {
      console.warn(
        'Custom cart: /cart/add.js returned no line key for the product, so the ' +
          'add-on cannot use Shopify native nesting. It will still nest in this ' +
          'cart via its group property, but not in checkout.'
      );
    }

    try {
      const res = await fetch(this.routeRoot() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [line] }),
      });
      const json = await res.json();
      if (json && json.status && Number(json.status) >= 400) {
        throw new Error(json.description || json.message || 'Warranty add failed');
      }
    } catch (err) {
      console.error('Custom cart: lifetime warranty could not be added', err);
    }
  }

  /* ---------------------------------------------------------- product detail */

  async fetchProductData(handle) {
    if (this.productCache[handle]) {
      return this.productCache[handle];
    }
    try {
      const res = await fetch(`${this.routeRoot()}products/${handle}.js`);
      if (!res.ok) return null;
      const data = await res.json();
      this.productCache[handle] = data;
      return data;
    } catch (err) {
      console.error('Custom cart: error fetching product details', err);
      return null;
    }
  }

  async showProductDetails(handle) {
    this.showLoading();
    try {
      const product = await this.fetchProductData(handle);
      if (!product) return;

      this.isDetailView = true;

      const backBtn = this.querySelector('[data-cart-back]');
      if (backBtn) backBtn.classList.remove('is-hidden');

      const bodyEl = this.querySelector('.custom-cart__body');
      if (!bodyEl) return;

      const imageSrc = product.featured_image || (product.images && product.images[0]) || '';
      const hasVariants = product.variants && product.variants.length > 1;
      const firstVariantId = product.variants && product.variants[0] ? product.variants[0].id : '';

      // product.title and product.description come from Shopify's own product JSON
      // for a handle this theme rendered, and the description is merchant-authored
      // HTML that is meant to render as HTML — the same trust boundary as
      // {{ product.description }} in Liquid.
      bodyEl.innerHTML = `
        <div class="custom-cart__detail-view">
          ${imageSrc ? `<img src="${imageSrc}" alt="" class="custom-cart__detail-img">` : ''}
          <h3 class="custom-cart__detail-title">${product.title}</h3>
          <p class="custom-cart__detail-price">${this.formatMoney(product.price)}</p>
          <button class="${this.primaryButtonClass('custom-cart__detail-atc')} custom-cart__detail-atc-btn" data-fbt-atc data-handle="${product.handle}" data-variant-id="${firstVariantId}" data-has-variants="${hasVariants}">
            <span>${this.t('add_to_cart', 'Add to cart')}</span>
          </button>
          <div class="custom-cart__detail-desc">${product.description || ''}</div>
        </div>
      `;
    } catch (err) {
      console.error('Custom cart: error showing product details', err);
    } finally {
      this.hideLoading();
    }
  }

  async hideProductDetails() {
    if (!this.isDetailView) return;

    this.isDetailView = false;
    const backBtn = this.querySelector('[data-cart-back]');
    if (backBtn) backBtn.classList.add('is-hidden');

    await this.refreshCart();
  }

  /* ------------------------------------------------------------ variant modal */

  async openVariantModal(handle) {
    this.showLoading();
    try {
      const product = await this.fetchProductData(handle);
      if (!product) return;

      const availableVariants = (product.variants || []).filter((v) => v.available);
      if (availableVariants.length === 0) {
        alert(this.t('sold_out', 'Sold out'));
        return;
      }

      if (availableVariants.length === 1) {
        this.addVariant(availableVariants[0].id);
        return;
      }

      const modalEl = this.querySelector('[data-variant-modal]');
      const contentEl = this.querySelector('[data-variant-modal-content]');
      if (!modalEl || !contentEl) return;

      const imageSrc = product.featured_image || (product.images && product.images[0]) || '';
      const optionLabel = product.options && product.options[0]
        ? (product.options[0].name || product.options[0])
        : this.t('options', 'Options');

      const optionsHtml = availableVariants
        .map((v) => `<option value="${v.id}">${v.title} - ${this.formatMoney(v.price)}</option>`)
        .join('');

      contentEl.innerHTML = `
        <button class="custom-cart__variant-modal-close" data-variant-modal-close aria-label="${this.t('close', 'Close')}">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true" focusable="false">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        <div class="custom-cart__variant-modal-row">
          <div class="custom-cart__variant-modal-img" style="background-image: url('${imageSrc}');"></div>
          <div class="custom-cart__variant-modal-info">
            <span class="custom-cart__variant-modal-title">${product.title}</span>
            <span class="custom-cart__variant-modal-price">${this.formatMoney(product.price)}</span>
          </div>
        </div>
        <label class="custom-cart__variant-label" for="CustomCartVariantSelect">${optionLabel}</label>
        <select class="custom-cart__variant-select" id="CustomCartVariantSelect" data-variant-select>
          ${optionsHtml}
        </select>
        <button class="${this.primaryButtonClass('custom-cart__variant-confirm-btn')} custom-cart__variant-confirm" data-variant-confirm>
          <span>${this.t('confirm', 'Confirm')}</span>
        </button>
      `;
      modalEl.classList.add('is-open');
      modalEl.setAttribute('aria-hidden', 'false');
    } catch (err) {
      console.error('Custom cart: error opening variant modal', err);
    } finally {
      this.hideLoading();
    }
  }

  closeVariantModal() {
    const modalEl = this.querySelector('[data-variant-modal]');
    if (!modalEl) return;
    modalEl.classList.remove('is-open');
    modalEl.setAttribute('aria-hidden', 'true');
  }

  /* ----------------------------------------------------------------- render */

  async fetchCartSection() {
    try {
      const res = await fetch(this.routeRoot() + '?sections=custom-cart');
      if (!res.ok) return null;
      const data = await res.json();
      return data['custom-cart'] || null;
    } catch (err) {
      console.error('Custom cart: error fetching custom-cart section', err);
      return null;
    }
  }

  renderCartSectionHtml(sectionHtml) {
    if (!sectionHtml) return;

    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const newCart = doc.querySelector('custom-cart');
    if (!newCart) return;

    const backBtn = this.querySelector('[data-cart-back]');
    if (backBtn && !this.isDetailView) {
      backBtn.classList.add('is-hidden');
    }

    const newBody = newCart.querySelector('.custom-cart__body');
    const currentBody = this.querySelector('.custom-cart__body');
    if (newBody && currentBody) {
      currentBody.innerHTML = newBody.innerHTML;
    }

    const newFooter = newCart.querySelector('#custom-cart-footer');
    const currentFooter = this.querySelector('#custom-cart-footer');

    if (newFooter && currentFooter) {
      currentFooter.innerHTML = newFooter.innerHTML;
      currentFooter.hidden = false;
    } else if (newFooter && !currentFooter) {
      // The footer is not rendered at all for an empty cart, so it has to be
      // recreated the first time an item is added.
      const card = this.querySelector('.custom-cart__card');
      if (card) {
        const footer = document.createElement('div');
        footer.className = 'custom-cart__footer';
        footer.id = 'custom-cart-footer';
        footer.innerHTML = newFooter.innerHTML;
        card.appendChild(footer);
      }
    } else if (!newFooter && currentFooter) {
      currentFooter.remove();
    }

    this.applyConvertedThreshold();

    // Confetti on the transition into the unlocked state, not on every render of
    // an already-unlocked cart.
    const msgEl = this.querySelector('[data-free-shipping-msg]');
    if (msgEl) {
      const isNowUnlocked = msgEl.dataset.unlocked === 'true';
      if (isNowUnlocked && !this.isUnlockedState) {
        this.triggerFallingConfetti();
      }
      this.isUnlockedState = isNowUnlocked;
    }

    // The footer was just replaced, so the policy checkbox is a new element.
    this.syncPolicyState();
  }

  /**
   * Fetches and applies the server-rendered section.
   *
   * `seq` is the render token taken when the mutation started. The section is
   * rendered server-side from whatever the cart looked like when the request
   * arrived, so a response that comes back after a newer mutation has begun is
   * already out of date — applying it snaps the drawer back to the previous
   * state, which is what "the cart lags and then reverts" looked like. Called
   * with no token (an error recovery, or an external caller) it always applies.
   */
  async renderCartState(seq) {
    const sectionHtml = await this.fetchCartSection();

    if (seq !== undefined && seq !== this.renderSeq) return;
    if (!sectionHtml) return;

    this.renderCartSectionHtml(sectionHtml);

    // The markup now agrees with the server, so the optimistic quantities have
    // served their purpose. Cleared only for a current render — a stale one
    // returned above without touching them.
    this.pendingQty.clear();
  }
}

/**
 * Line item property recording the variant a package upgrade came from. Shopify
 * convention: a leading underscore marks the property private, which is also what
 * keeps it out of the visible properties list in
 * snippets/custom-cart-drawer.liquid. Kept in step with the same key read in
 * snippets/custom-cart-package-upgrade.liquid.
 */
CustomCart.UPGRADE_ORIGIN_KEY = '_upgrade_from';

/**
 * Pairing keys for add-ons nested in THIS cart.
 *
 * The parent carries NEST_GROUP_KEY and its add-on carries NEST_PARENT_KEY with
 * the same token. snippets/custom-cart-drawer.liquid matches the two to draw the
 * add-on underneath the line it belongs to.
 *
 * Shopify's own nested cart lines are still used in parallel — the add sends
 * `parent_line_key`, which is what nests the line in checkout, the order status
 * page, the confirmation emails and the admin. But that relationship is not
 * readable from Liquid on this storefront (`parent_relationship.parent_key` comes
 * back empty even when checkout nests correctly), so the drawer cannot render from
 * it. These properties are ours and are proven to work: the same snippet already
 * reads `_shipping_protection` off a line.
 *
 * The token is minted per add rather than derived from the variant, so the same
 * product can sit in the cart twice — once with an add-on and once without.
 * Differing properties are also what stops Shopify merging those two into one line.
 *
 * Both are `_`-prefixed, so they are private: hidden from the shopper, still on the
 * order for fulfilment, and already skipped by the drawer's visible-property list.
 */
CustomCart.NEST_GROUP_KEY = '_cc_group';
CustomCart.NEST_PARENT_KEY = '_cc_parent';

/**
 * Console helper for the warranty wiring, because the failure mode is invisible:
 * a block that cannot be matched to its form looks exactly like a block the
 * shopper chose not to tick.
 *
 * Run `CustomCartDebug.warranty()` on a product page.
 */
window.CustomCartDebug = {
  warranty() {
    const cart = document.querySelector('custom-cart');
    const forms = Array.from(document.querySelectorAll('form[action*="/cart/add"]'));
    const blocks = Array.from(document.querySelectorAll('[data-lifetime-warranty]'));

    const report = {
      cartElementPresent: !!cart,
      cartType: (window.theme && theme.settings && theme.settings.cartType) || '(unknown)',
      forms: forms.map((f) => ({ id: f.id || '(no id)', action: f.getAttribute('action') })),
      blocks: blocks.map((b) => {
        const toggle = b.querySelector('[data-warranty-toggle]');
        return {
          expectsFormId: b.getAttribute('data-warranty-form') || '(none)',
          hasToggle: !!toggle,
          ticked: toggle ? toggle.checked : null,
          variantId: toggle ? toggle.dataset.warrantyVariantId || '(none)' : null,
        };
      }),
      matches: forms.map((f) => {
        const found = cart && cart.findWarrantyBlock ? cart.findWarrantyBlock(f) : null;
        return { formId: f.id || '(no id)', matchedBy: found ? found.how : 'NO MATCH' };
      }),
    };

    console.table(report.forms);
    console.table(report.blocks);
    console.table(report.matches);
    return report;
  },
};

if (!customElements.get('custom-cart')) {
  customElements.define('custom-cart', CustomCart);
}
